import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { pricingStatisticsQueryOptions, usePricingStatistics } from '~/api/statistics';
import { Card, Illustration, Spinner, Text } from '~/components';
import { Field, Select } from '~/components/Form';
import { useIntl } from '~/hooks/useIntl';
import {
  illustrationStyle,
  illustrationWrapperStyle,
  pricingNumberStyle,
  pricingStatsStyle,
  pricingTextStyle,
  pricingTitleStyle,
  yearSelectRowStyle,
} from '~/pages/Settings/Pricing/PricingStats.css';

export const PricingStats = () => {
  const { data: statistics, isError, isPending } = usePricingStatistics();
  const queryClient = useQueryClient();

  const [year, setYear] = useState(() => dayjs().year());
  const currentYear = dayjs().year();

  const { formatCurrency } = useIntl();
  const { t } = useTranslation(['pricing']);

  const yearOptions = useMemo(
    () =>
      Object.keys(statistics?.totalAdvantageByYear ?? {})
        .map((year) => ({ label: year, value: +year }))
        .sort((a, b) => b.value - a.value),
    [statistics?.totalAdvantageByYear],
  );

  return (
    <>
      {yearOptions.length > 1 && (
        <div className={yearSelectRowStyle}>
          <Field.Root>
            <Select.Root onValueChange={(nextYear) => setYear(nextYear ?? dayjs().year())} value={year}>
              <Select.Button />

              <Select.List>
                {yearOptions.map(({ label, value }) => (
                  <Select.Item key={value} value={value}>
                    {label}
                  </Select.Item>
                ))}
              </Select.List>
            </Select.Root>
          </Field.Root>
        </div>
      )}

      <Card.Root className={pricingStatsStyle} render={<article />} variant="secondary-brand">
        {!isError ? (
          <>
            <div className={illustrationWrapperStyle}>
              <Illustration aria-hidden className={illustrationStyle} name="Copyright" strokeWidth={1.4} />
            </div>

            <Text className={pricingNumberStyle}>
              {isPending ? <Spinner color="default" size="inline" /> : formatCurrency(statistics?.totalAdvantageByYear[year] ?? 0)}
            </Text>

            <h2 className={pricingTitleStyle}>
              {year === currentYear
                ? t('pricing:pricingStats.currentYear.title', { year })
                : t('pricing:pricingStats.previousYear.title', { year })}
            </h2>

            <Text className={pricingTextStyle}>
              {year === currentYear ? t('pricing:pricingStats.currentYear.text') : t('pricing:pricingStats.previousYear.text')}
            </Text>
          </>
        ) : (
          <Card.Error
            onRetry={() => queryClient.resetQueries(pricingStatisticsQueryOptions)}
            title={t('pricing:pricingStats.error.title')}
          />
        )}
      </Card.Root>
    </>
  );
};
